import React, { createContext, useContext, useEffect, useState } from "react";
import { Auth, Hub } from "aws-amplify";

const AUTH_CHANNEL = "auth";

export const AuthContext = createContext(null);

export const AuthProvider = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    setCurrentSession();

    const hubCallback = async ({ payload: { event } }) => {
      // eslint-disable-next-line default-case
      switch (event) {
        case "signIn":
        case "cognitoHostedUI":
          setCurrentSession();
          break;
        case "signOut":
        case "signIn_failure":
        case "tokenRefresh_failure":
          setSession(null);
          setIsLoading(false);
          break;
      }
    };

    Hub.listen(AUTH_CHANNEL, hubCallback);

    return () => {
      Hub.remove(AUTH_CHANNEL, hubCallback);
    };
  }, []);

  async function setCurrentSession() {
    return Auth.currentSession()
      .then(setSession)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log("no user!");
      })
      .finally(() => setIsLoading(false));
  }

  const value = React.useMemo(
    () => ({
      setSession,
      session,
    }),
    [session, setSession]
  );

  return (
    <AuthContext.Provider value={value}>
      {isLoading && "Loading..."}
      {!isLoading && props.children}
    </AuthContext.Provider>
  );
};

export function useAuthContext() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be inside AuthProvider");
  }

  return context;
}

function useAuth() {
  const { session } = useAuthContext();

  async function federatedLogin(provider) {
    return Auth.federatedSignIn({ provider });
  }

  async function getAuthToken() {
    if (!session) {
      return null;
    }

    const idTokenExpire = session.getIdToken().getExpiration();
    const refreshToken = session.getRefreshToken();
    const currentTimeSeconds = Math.round(+new Date() / 1000);

    return new Promise((resolve) => {
      // From here https://github.com/aws-amplify/amplify-js/issues/2560#issuecomment-657251846
      if (idTokenExpire < currentTimeSeconds) {
        Auth.currentAuthenticatedUser().then((res) => {
          res.refreshSession(refreshToken, (err, data) => {
            if (err) {
              Auth.signOut();
            } else {
              resolve(data.getIdToken().getJwtToken());
            }
          });
        });
      } else {
        resolve(session.getIdToken().getJwtToken());
      }
    });
  }

  function loginWithEntrata(authToken, referrer) {
    // const getToken = async (authToken, referrer) => {
    // };

    // return getToken(authToken, referrer);
    return;
  }

  async function logout() {
    return Auth.signOut();
  }

  function isLoggedIn() {
    return !!session;
  }

  function isSuperAdmin() {
    if (!session) {
      return false;
    }

    // Users with no Cognito groups (e.g. a freshly approved admin) have no
    // `cognito:groups` claim, so guard against it being undefined.
    const groups = session.getAccessToken().payload["cognito:groups"];

    return Array.isArray(groups) && groups.indexOf("Super_Admin") !== -1;
  }

  function isSuperAdminRead() {
    if (!isSuperAdmin()) {
      return false;
    }

    return (
      session
        .getAccessToken()
        .payload["cognito:groups"].indexOf("Super_Admin_Read") !== -1
    );
  }

  function isSuperAdminWrite() {
    if (!isSuperAdmin()) {
      return false;
    }

    return (
      session
        .getAccessToken()
        .payload["cognito:groups"].indexOf("Super_Admin_Write") !== -1
    );
  }

  function isWriteAccess() {
    return isSuperAdmin() ? isSuperAdminWrite() : true;
  }

  return {
    isWriteAccess,
    logout,
    isLoggedIn,
    getAuthToken,
    isSuperAdmin,
    federatedLogin,
    isSuperAdminWrite,
    loginWithEntrata,
    isSuperAdminRead,
  };
}

export default useAuth;
